import { Injectable } from "@angular/core";
import { MutexService } from "common/concurrency/mutex.service";
import { ProcessVersionsManagementResource } from "core/stages-client";
import { LinkPluginComponent } from "process/description/link-plugin/link-plugin.component";
import { PluginRegistry } from "common/editor/description.service";
import { HtmlTemplatePluginComponent } from "process/description/htmltemplate-plugin/htmltemplate-plugin.component";

type DisplayState = stages.administration.processrelease.DisplayState;
type JobIdentifier = stages.core.scheduler.JobIdentifier;
type ProcessVersion = stages.management.processversions.ProcessVersion;
type ProcessVersionProgressInfo =
	stages.management.processversions.main.ProcessVersionsManagementResourceProcessVersionProgressInfo;
type Validity = stages.management.processversions.Validity;
type ProcessVersionCreationSettings = stages.management.processversions.ProcessVersionCreationSettings;
type CreateProcessInterfaceVersionSettings = stages.management.processversions.CreateProcessInterfaceVersionSettings;
type ValidatorType = stages.process.validation.ValidatorType;

@Injectable({ providedIn: "root" })
export class VersionService {
	private pluginRegistry = {
		stageslink: LinkPluginComponent,
		stageshtmltemplate: HtmlTemplatePluginComponent,
	};

	constructor(
		private readonly mutexService: MutexService,
		private readonly processVersionsManagementResource: ProcessVersionsManagementResource,
	) {}

	/**
	 * Returns all process versions for the specified workspace id, page and page size.
	 */
	async getProcessVersions(
		currentWorkspaceId: string,
		page: number,
		isPolling: boolean,
		pageSize = 10,
	): Promise<SecuredPagedResultAndInfo<ProcessVersion, ProcessVersionProgressInfo>> {
		if (isPolling) {
			this.processVersionsManagementResource.setHeadersForNextRequest({ "Stages-Polling": "true" });
		}
		return this.processVersionsManagementResource.getProcessVersions(currentWorkspaceId, {
			page: page,
			pageSize: pageSize,
		});
	}

	async createProcessVersion(
		currentWorkspaceId: string,
		currentPv: string,
		settings: ProcessVersionCreationSettings,
	): Promise<JobIdentifier> {
		return this.mutexService.invokeWithResult("createProcessVersion", async () => {
			this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
				pv: currentPv,
			});
			return this.processVersionsManagementResource.createProcessVersion(currentWorkspaceId, settings);
		});
	}

	/**
	 * Returns the process version states that are allowed for newly created process versions for the current user in the given workspace.
	 */
	async getCreatableProcessVersionStates(workspaceId: string, pv: string): Promise<DisplayState[]> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.getCreatableProcessVersionStates(workspaceId);
	}

	async getReleaseAsValidVersionProperties(
		workspaceId: string,
		pv: string,
		processVersionId: string,
	): Promise<stages.management.processversions.SetStateSettingsAndInfo> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.getReleaseAsValidVersionProperties(workspaceId, processVersionId);
	}

	async deleteProcess(workspaceId: string, pv: string): Promise<JobIdentifier> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.deleteProcess(workspaceId);
	}

	async deleteProcessVersions(
		workspaceId: string,
		pv: string,
		processVersionIds: string[],
		page: number = 1,
	): Promise<SecuredPagedResult<ProcessVersion>> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.deleteProcessVersions(workspaceId, processVersionIds, {
			page: page,
			pageSize: 10,
		});
	}

	async replaceWorkingVersion(workspaceId: string, pv: string, processVersionId: string): Promise<JobIdentifier> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.replaceWorkingVersion(workspaceId, processVersionId);
	}

	async releaseAsValidVersion(
		workspaceId: string,
		pv: string,
		processVersionId: string,
		state: string,
	): Promise<JobIdentifier> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.releaseAsValidVersion(workspaceId, processVersionId, {
			stateTypeIdent: state,
		});
	}

	async releaseInterfaces(
		workspaceId: string,
		pv: string,
		settings: CreateProcessInterfaceVersionSettings,
	): Promise<JobIdentifier> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.releaseInterfaces(workspaceId, settings);
	}

	async archive(workspaceId: string, processId: string, pv: string, password: string): Promise<JobIdentifier> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
			workspaceId: workspaceId,
		});
		return this.processVersionsManagementResource.archive(processId, { password: password });
	}

	async setState(
		workspaceId: string,
		pv: string,
		processVersionId: string,
		state: string,
	): Promise<JobIdentifier | null> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.setState(workspaceId, processVersionId, {
			stateTypeIdent: state,
		});
	}

	async renameProcessVersion(
		currentWorkspaceId: string,
		pv: string,
		processVersionId: string,
		processVersionName: string,
	): Promise<void> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.saveRenamedProcessVersion(currentWorkspaceId, processVersionId, {
			name: processVersionName,
		});
	}

	/**
	 * Performs various validations on the working revision of this workspace.
	 */
	async validateProcessVersion(workspaceId: string, pv: string): Promise<JobIdentifier[]> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.validateProcess(workspaceId);
	}

	/**
	 * Fetches all configured validation checks for all instances of the process
	 */
	async getProcessValidators(workspaceId: string, pv: string): Promise<ValidatorType[]> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.getValidatorsForProcess(workspaceId);
	}

	async updateValidVersionConsiderFrozen(
		workspaceId: string,
		newValidVersionName: string,
		currentWorkspaceId: string,
		pv: string,
	): Promise<JobIdentifier[]> {
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			workspaceId: currentWorkspaceId,
			pv: pv,
		});
		return this.processVersionsManagementResource.updateValidVersionConsiderFrozen(workspaceId, [], {
			newValidVersionName: newValidVersionName,
		});
	}

	async getValidities(
		currentWorkspaceId: string,
		pv: string,
		page: number,
		isPolling: boolean,
		pageSize = 10,
	): Promise<SecuredPagedResult<Validity>> {
		if (isPolling) {
			this.processVersionsManagementResource.setHeadersForNextRequest({ "Stages-Polling": "true" });
		}
		this.processVersionsManagementResource.setAdditionalHttpParamsForNextRequest({
			pv: pv,
		});
		return this.processVersionsManagementResource.getValidities(currentWorkspaceId, {
			page: page,
			pageSize: pageSize,
		});
	}

	async deleteFailedOperation(currentWorkspaceId: string, jobName: string): Promise<void> {
		await this.processVersionsManagementResource.deleteProcessVersions(currentWorkspaceId, [jobName], {
			page: 1,
			pageSize: 10,
		});
	}

	async clearWarnings(jobName: string, operationType: string): Promise<void> {
		await this.processVersionsManagementResource.clearWarnings(jobName, operationType);
	}

	getPluginRegistry(): PluginRegistry {
		return this.pluginRegistry;
	}
}
